import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { FloatingInput } from '@/components/ui/floating-input';
import { TransactionBuilderFooter } from './TransactionBuilderFooter';
import { TransactionBuilderPolicyIndicator } from './TransactionBuilderPolicyIndicator';
import {
  normalizeRawTransaction,
  type RawTransaction,
} from './raw-transaction';
import {
  TransactionBuilderPageType,
  useTransactionBuilderStore,
} from './useTransactionBuilderStore';

/** JSON children retain exact bytes rather than inventing a decoded form. */
export function RawBatchTransactionEditor({
  initialValue,
  onSave,
}: {
  initialValue: RawTransaction;
  onSave: (transaction: RawTransaction) => void;
}) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const policy = useTransactionBuilderStore((state) => state.policy);
  const navigate = useTransactionBuilderStore(
    (state) => state.navigateForwardToPage
  );
  return (
    <div className="flex min-h-full flex-col">
      <div className="mx-auto flex w-full max-w-[738px] flex-1 flex-col gap-[30px] px-[30px] py-[18px]">
        {policy && <TransactionBuilderPolicyIndicator policy={policy} />}
        <FloatingInput
          id="raw-to"
          label="To"
          value={value.to}
          onChange={(event) => setValue({ ...value, to: event.target.value })}
        />
        <FloatingInput
          id="raw-value"
          label="Value (wei)"
          value={value.value}
          onChange={(event) =>
            setValue({ ...value, value: event.target.value })
          }
        />
        <FloatingInput
          id="raw-data"
          label="Data"
          variant="textarea"
          value={value.data}
          onChange={(event) => setValue({ ...value, data: event.target.value })}
          className="min-h-36 font-mono"
        />
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
      <TransactionBuilderFooter
        embedded
        continueButtonText="Save changes"
        onContinue={() => {
          try {
            onSave(normalizeRawTransaction(value));
          } catch (cause) {
            setError(
              cause instanceof Error ? cause.message : 'Invalid transaction.'
            );
          }
        }}
        secondaryAction={{
          label: 'Back to policies',
          icon: <ChevronLeft className="size-[15px]" />,
          onClick: () =>
            navigate({
              page: TransactionBuilderPageType.POLICY_SELECTION_PAGE,
            }),
        }}
      />
    </div>
  );
}
